// ROW 1 — TEXT ("the words") as DATA. CONCEPT-text-ribbon §3; R2.5 FLYOUT
// RECOMPOSITION 2026-07-20 (owner: "a series of neat buttons with flyouts");
// R3 ROW-2 REWORK same day: Color and Styles★ MOVED to Row 2's Style section
// (owner §1/§7 — one color law for every object, universal styles coming),
// and the B/I/U/S cluster grew the superscript/subscript flyout (owner §6).
//
// Schema + handler-key contract: core/rowsetSchema.mjs (validated by the unit
// suite). Controls reference HANDLER KEYS and MIRROR KEYS only — zero live
// imports; the mount owns the resolution table.
//
// The R2.5 shape of the row: order stays §3, but every multi-option control
// is now ONE house-sized button with a corner-chip flyout (RibbonFlyout):
//   size    editable field + slider flyout (no ± steppers)
//   weight  B fast-path toggle; corner chip lists the FONT'S real weights
//   script  one x² button; flyout Superscript/Subscript/Normal (3D-gated)
//   case    one Aa button; flyout aa/Aa/AA/as-typed
//   lists   one button; flyout bullets/numbers/checkboxes/A.B.C./none
//   align   one button (face = current alignment); flyout of the four
//   spacing one button; flyout of the two sliders
//   curve   one button (face = current bend); flyout of the four presets
// Groups whose single control is a flyout carry their drawer link INSIDE the
// flyout footer (RibbonGroup suppresses the corner "…" — no bare dots).
//
// Collapse behavior (§3, unchanged law): sacrifice order Fit → Spacing →
// Curve → Lists → Case (numbered 1–5), then unnumbered right-to-left
// (subject → align). Scope, font, size and style NEVER collapse. With every
// group one button wide, the folding-must-help guard skips most of them
// (their labeled chips would be WIDER) — the row simply fits far deeper.

const textRow1 = {
  id: 'text-row1',
  row: 1,
  groups: [
    {
      id: 'scope',
      label: 'Scope',
      drawerSection: null,
      neverCollapse: true,
      controls: [
        // ScopeChip — reads state.scope ({kind:'all'|'word'|'chars',…}),
        // widening fires scope.set('word'|'all'). Live since R2.
        { id: 'scopeChip', kind: 'scope', label: 'Styling scope', read: 'scope', handler: 'scope.set' },
      ],
    },
    {
      id: 'font',
      label: 'Font',
      // The FontPicker trigger IS the name-faced flyout button (search,
      // previews, recently-used) — already the R2.5 pattern.
      drawerSection: null,
      neverCollapse: true,
      controls: [
        {
          id: 'fontFamily', kind: 'font', label: 'Font', read: 'fontFamily',
          handler: 'text.setFontFamily', commit: 'defer', fallback: 'Arial',
        },
      ],
    },
    {
      id: 'size',
      label: 'Size',
      // Drawer 'size' section arrives with R4's distance picker.
      drawerSection: null,
      neverCollapse: true,
      controls: [
        // R2.5: editable field ONLY; focus/click opens the slider flyout.
        // Typing → text.setFontSize ('soon', engine bounds 4–2000 kept for
        // existing designs); sliding → text.setFontSizeLive ('defer') with
        // ONE commit on release. Slider spans the sane print range 6–600.
        // pt is the only unit this phase (R4 wires in/cm + readable-from);
        // the field tooltip and the flyout label say "pt" without a dead
        // one-option select pretending choice.
        {
          id: 'fontSize', kind: 'sizefield', label: 'Font size', read: 'fontSize',
          min: 4, max: 2000, step: 1, sliderMin: 6, sliderMax: 600,
          handler: 'text.setFontSize', slideHandler: 'text.setFontSizeLive',
          commit: 'soon', fallback: 32,
        },
      ],
    },
    {
      id: 'style',
      label: 'Style',
      drawerSection: null,
      neverCollapse: true,
      controls: [
        // B is a FAST PATH (click = bold/regular flip, the live toolbar's
        // rule); its corner chip opens the weights flyout listing the FONT'S
        // available faces (mount feeds fontWeightOptions from
        // getFontWeights(effFontFamily)), current weight marked.
        {
          id: 'bold', kind: 'flyoutchips', label: 'Bold — more thicknesses in the corner menu',
          icon: 'bold', read: 'fontWeight', optionsFrom: 'fontWeightOptions',
          handler: 'text.setFontWeight', faceHandler: 'text.toggleBold', commit: 'defer',
          options: [{ value: '400', label: 'Regular' }, { value: '700', label: 'Bold' }],
          activeWhen: { read: 'fontWeight', gt: 500 }, fallback: '400',
        },
        { id: 'italic', kind: 'toggle', label: 'Italic', icon: 'italic', handler: 'text.toggleItalic', activeWhen: { read: 'fontStyle', eq: 'italic' } },
        { id: 'underline', kind: 'toggle', label: 'Underline', icon: 'underline', handler: 'text.toggleUnderline', activeWhen: 'underline' },
        { id: 'strikethrough', kind: 'toggle', label: 'Strikethrough', icon: 'strikethrough', handler: 'text.toggleStrikethrough', activeWhen: 'linethrough' },
        // Superscript/subscript (owner §6): one x² button; the flyout holds
        // the three script modes (engine setTextScript — selection-aware:
        // the highlighted run while editing, the whole string otherwise).
        // 3D-gated exactly like the drawer's Raised & lowered chips: the
        // bake has one baseline, so the control disables with a plain-words
        // tooltip instead of doing nothing silently.
        {
          id: 'textScript', kind: 'flyoutchips', label: 'Raised & lowered — superscript and subscript',
          text: 'x²', read: 'textScript', handler: 'text.setScript', faceFromOptions: true,
          activeWhen: { read: 'textScript', neq: 'normal' },
          disabledWhen: 'is3dActive',
          disabledTitle: 'Raised and lowered letters work on flat text — turn 3D off to use them',
          drawerSection: 'script',
          options: [
            { value: 'super', label: 'Superscript — like the 2 in x²', text: 'x²' },
            { value: 'sub', label: 'Subscript — like the 2 in H₂O', text: 'x₂' },
            { value: 'normal', label: 'Back on the line', text: 'x' },
          ],
          fallback: 'normal',
        },
      ],
    },
    {
      id: 'case',
      label: 'Case',
      drawerSection: null,
      collapsePriority: 5,
      controls: [
        // One Aa button (owner §4); the flyout holds the four reversible
        // letterCase recipe modes. Values are ENGINE ids.
        {
          id: 'letterCase', kind: 'flyoutchips', label: 'Letter case', text: 'Aa',
          read: 'letterCase', handler: 'text.setLetterCase',
          activeWhen: { read: 'letterCase', neq: 'none' },
          options: [
            { value: 'none', label: 'As typed', text: 'Aa' },
            { value: 'lower', label: 'lowercase', text: 'aa' },
            { value: 'upper', label: 'UPPERCASE', text: 'AA' },
            { value: 'title', label: 'Title Case', text: 'Aa Bb' },
          ],
          fallback: 'none',
        },
      ],
    },
    // (Color group MOVED to Row 2's Style section — owner §1: one color
    // button for every object; the per-char routing law rides with it.)
    {
      id: 'lists',
      label: 'Lists',
      // No deeper panel — the flyout IS the whole surface (no-twins).
      drawerSection: null,
      collapsePriority: 4,
      controls: [
        // One bullet button (owner §6); flyout: the four teaching flavors +
        // an explicit "No list". Values are ENGINE mode ids (data.list.mode
        // mirror drives the face + the ✓). Re-picking the live mode also
        // toggles off (mount rule, unchanged).
        {
          id: 'listMode', kind: 'flyoutchips', label: 'Lists', icon: 'listBullets',
          read: 'listMode', handler: 'text.setList', faceFromOptions: true,
          activeWhen: { read: 'listMode', neq: 'none' },
          options: [
            { value: 'bullet', label: 'Bulleted list', icon: 'listBullets' },
            { value: 'number', label: 'Numbered list', icon: 'listNumbers' },
            { value: 'checkbox', label: 'Checkbox list (routines)', icon: 'listCheckboxes' },
            { value: 'letter', label: 'A. B. C. list (choices)', icon: 'listLetters' },
            { value: 'none', label: 'No list', icon: 'listNone' },
          ],
          fallback: 'none',
        },
      ],
    },
    {
      id: 'align',
      label: 'Align',
      drawerSection: null,
      controls: [
        // One button whose face is the CURRENT alignment; flyout of four.
        {
          id: 'textAlign', kind: 'flyoutchips', label: 'Alignment', icon: 'alignLeft',
          read: 'textAlign', handler: 'text.setAlign', faceFromOptions: true,
          options: [
            { value: 'left', label: 'Align text left', icon: 'alignLeft' },
            { value: 'center', label: 'Center text', icon: 'alignCenter' },
            { value: 'right', label: 'Align text right', icon: 'alignRight' },
            { value: 'justify', label: 'Even edges', icon: 'alignJustify' },
          ],
          fallback: 'left',
        },
      ],
    },
    {
      id: 'spacing',
      label: 'Spacing',
      drawerSection: 'spacing',
      collapsePriority: 2,
      controls: [
        // One button → two sliders (owner §6): defer ticks, ONE history
        // entry per drag (history.commitNow on release). The drawer keeps
        // the deep spacing panel ('More options…' inside the flyout).
        {
          id: 'spacingFly', kind: 'flyoutsliders', label: 'Spacing', icon: 'letterSpacing',
          sliders: [
            { id: 'letterSpacing', label: 'Letter spacing', read: 'charSpacing', min: -200, max: 800, step: 10, handler: 'text.setLetterSpacing', fallback: 0 },
            { id: 'lineHeight', label: 'Line height', read: 'lineHeight', min: 0.5, max: 3, step: 0.05, handler: 'text.setLineHeight', fallback: 1.16 },
          ],
        },
      ],
    },
    {
      id: 'curve',
      label: 'Curve',
      drawerSection: 'shape',
      collapsePriority: 3,
      controls: [
        // One button (face = current bend); flyout of the four presets.
        // The drawer 'shape' section keeps the amount fine-tune (footer).
        {
          id: 'curvePreset', kind: 'flyoutchips', label: 'Curve', icon: 'curveStraight',
          read: 'curvePreset', handler: 'text.setCurvePreset', faceFromOptions: true,
          activeWhen: { read: 'curvePreset', neq: 'straight' },
          options: [
            { value: 'straight', label: 'Straight', icon: 'curveStraight' },
            { value: 'arc', label: 'Arc', icon: 'curveArc' },
            { value: 'circle', label: 'Circle', icon: 'curveCircle' },
            { value: 'wave', label: 'Wave', icon: 'curveWave' },
          ],
          fallback: 'straight',
        },
      ],
    },
    {
      id: 'subject',
      label: 'Subject',
      drawerSection: null,
      dot: 'subjectActive',
      placeholder: true,
      controls: [
        // SUBJECT placeholder (CONCEPT-subject-toolsets): one labeled button
        // straight into the ELA tools accordion — the button IS the drawer
        // link, so no separate corner. R3's manifest registry replaces this.
        { id: 'subjectTools', kind: 'button', label: 'ELA tools (color vowels, syllables, blends)', text: 'ELA', handler: 'subject.openTools' },
      ],
    },
    {
      id: 'fit',
      label: 'Fit',
      // One-shot action; the drawer's Fit accordion (reachable in the
      // drawer itself) explains the behavior — a corner "…" for one link
      // was the crowding the owner called out.
      drawerSection: null,
      collapsePriority: 1,
      controls: [
        { id: 'fitToBox', kind: 'button', label: 'Fit text to its box', icon: 'fitBox', handler: 'text.fitToBox' },
      ],
    },
    // (Styles group MOVED to Row 2's Style section — owner §7: the same
    // styles are coming for shapes and icons, so the button lives on the
    // universal row now.)
  ],
};

export default textRow1;
